import { useMemo, useRef, useState, useEffect } from 'react';
import { AlertCircle, Loader2, Trash2 } from 'lucide-react';

function activeConnectionIdFromProps({ user, connectionsList, selectedConnectionId }) {
  if (user) return null;
  if (!connectionsList?.length) return null;
  return connectionsList.some((c) => c.id === selectedConnectionId) ? selectedConnectionId : connectionsList[0]?.id || null;
}

export default function TimeCardHcmDeletePanel({
  apiBase = '',
  user,
  connectionsList = [],
  selectedConnectionId,
  onSelectConnectionId,
  /** { personNumber?: string, startDate?: string, stopDate?: string } — from Time cards list or payroll; applied once. */
  prefill = null,
  onPrefillConsumed = null,
}) {
  const activeConnectionId = useMemo(
    () => activeConnectionIdFromProps({ user, connectionsList, selectedConnectionId }),
    [user, connectionsList, selectedConnectionId]
  );
  const activeConnection = connectionsList?.find((c) => c.id === activeConnectionId);

  const hasOracleTarget = !!user || (connectionsList && connectionsList.length > 0);

  const [mode, setMode] = useState('single'); // 'single' | 'bulk'

  const [personNumber, setPersonNumber] = useState('');
  const [startDate, setStartDate] = useState('');
  const [stopDate, setStopDate] = useState('');

  useEffect(() => {
    if (!prefill || typeof prefill !== 'object') return;
    if (prefill.personNumber != null && String(prefill.personNumber).trim() !== '') {
      setPersonNumber(String(prefill.personNumber).trim());
    }
    if (prefill.startDate) setStartDate(String(prefill.startDate).trim());
    if (prefill.stopDate) setStopDate(String(prefill.stopDate).trim());
    onPrefillConsumed?.();
  }, [prefill, onPrefillConsumed]);
  const [startTime, setStartTime] = useState('');
  const [stopTime, setStopTime] = useState('');
  const [toleranceMs, setToleranceMs] = useState('120000');

  const bulkInputRef = useRef(null);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkPreviewLoading, setBulkPreviewLoading] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkError, setBulkError] = useState(null);
  const [bulkConfirmChecked, setBulkConfirmChecked] = useState(false);
  const [bulkConfirmText, setBulkConfirmText] = useState('');

  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [preview, setPreview] = useState(null);

  const [selectedMatchKey, setSelectedMatchKey] = useState('');
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [deleteResult, setDeleteResult] = useState(null);

  const selectedMatch = useMemo(() => {
    const matches = Array.isArray(preview?.matches) ? preview.matches : [];
    if (!matches.length) return null;
    if (matches.length === 1) return matches[0];
    const key = String(selectedMatchKey || '').trim();
    if (!key) return null;
    return matches.find((m) => String(m.fingerprint) === key) || null;
  }, [preview, selectedMatchKey]);

  const resetOutcome = () => {
    setPreviewError(null);
    setDeleteError(null);
    setDeleteResult(null);
  };

  const resetBulkOutcome = () => {
    setBulkError(null);
    setBulkPreview(null);
    setBulkConfirmChecked(false);
    setBulkConfirmText('');
  };

  const postJsonWithNetworkFallback = async (apiPath, jsonBody) => {
    const attempted = [];
    const targets = [];
    const absolute = `${apiBase}${apiPath}`;
    if (apiBase) targets.push(absolute);
    targets.push(apiPath);

    let lastNetworkError = null;
    for (const target of targets) {
      if (attempted.includes(target)) continue;
      attempted.push(target);
      try {
        const res = await fetch(target, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(jsonBody),
        });
        return { res, attemptedUrls: attempted };
      } catch (err) {
        const msg = err?.message || '';
        const isNetworkError =
          msg === 'Failed to fetch' ||
          /network|fetch|ECONNREFUSED|Load failed|NetworkError/i.test(msg);
        if (!isNetworkError) throw err;
        lastNetworkError = err;
      }
    }
    const finalErr = new Error(lastNetworkError?.message || 'Failed to fetch');
    finalErr.attemptedUrls = attempted;
    throw finalErr;
  };

  const postMultipartWithNetworkFallback = async (apiPath, formData) => {
    const attempted = [];
    const targets = [];
    const absolute = `${apiBase}${apiPath}`;
    if (apiBase) targets.push(absolute);
    targets.push(apiPath);

    let lastNetworkError = null;
    for (const target of targets) {
      if (attempted.includes(target)) continue;
      attempted.push(target);
      try {
        const res = await fetch(target, { method: 'POST', credentials: 'include', body: formData });
        return { res, attemptedUrls: attempted };
      } catch (err) {
        const msg = err?.message || '';
        const isNetworkError =
          msg === 'Failed to fetch' ||
          /network|fetch|ECONNREFUSED|Load failed|NetworkError/i.test(msg);
        if (!isNetworkError) throw err;
        lastNetworkError = err;
      }
    }
    const finalErr = new Error(lastNetworkError?.message || 'Failed to fetch');
    finalErr.attemptedUrls = attempted;
    throw finalErr;
  };

  const handlePreview = async () => {
    resetOutcome();
    setPreview(null);
    setSelectedMatchKey('');
    setConfirmChecked(false);
    setConfirmText('');

    if (!hasOracleTarget) return;
    if (!personNumber.trim() || !startDate.trim()) {
      setPreviewError('Person number and start date are required.');
      return;
    }
    const st = startTime.trim();
    const et = stopTime.trim();
    if ((st && !et) || (!st && et)) {
      setPreviewError('Enter both start time and stop time, or leave both blank to list every line on the selected date(s).');
      return;
    }

    setPreviewLoading(true);
    try {
      const tol = Number(String(toleranceMs || '').trim());
      const body = {
        personNumber: personNumber.trim(),
        startDate: startDate.trim(),
        stopDate: stopDate.trim(),
        toleranceMs: Number.isFinite(tol) ? tol : 120000,
      };
      if (st && et) {
        body.startTime = st;
        body.stopTime = et;
      }
      if (activeConnectionId) body.connectionId = activeConnectionId;

      const { res } = await postJsonWithNetworkFallback('/api/hcm-timecards/delete-preview', body);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || data.message || `Preview failed (${res.status})`);
      }
      setPreview(data);
      const matches = Array.isArray(data.matches) ? data.matches : [];
      if (matches.length === 1) setSelectedMatchKey(String(matches[0].fingerprint));
    } catch (e) {
      const attemptedUrls = Array.isArray(e?.attemptedUrls) ? e.attemptedUrls : [];
      const detail = attemptedUrls.length ? ` Attempted: ${attemptedUrls.join(' , ')}` : '';
      setPreviewError((e.message || 'Preview failed') + detail);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleBulkPreview = async () => {
    resetBulkOutcome();
    if (!hasOracleTarget) return;
    if (!bulkFile) {
      setBulkError('Choose an Excel file first (.xlsx or .xls).');
      return;
    }

    setBulkPreviewLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', bulkFile);
      fd.append('toleranceMs', String(toleranceMs || '120000'));
      if (activeConnectionId) fd.append('connectionId', activeConnectionId);

      const { res } = await postMultipartWithNetworkFallback('/api/hcm-timecards/bulk-delete-preview', fd);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || data.message || `Bulk preview failed (${res.status})`);
      setBulkPreview(data);
    } catch (e) {
      const attemptedUrls = Array.isArray(e?.attemptedUrls) ? e.attemptedUrls : [];
      const detail = attemptedUrls.length ? ` Attempted: ${attemptedUrls.join(' , ')}` : '';
      setBulkError((e.message || 'Bulk preview failed') + detail);
    } finally {
      setBulkPreviewLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    setBulkError(null);

    if (!bulkFile) {
      setBulkError('Choose an Excel file first (.xlsx or .xls).');
      return;
    }
    if (!bulkConfirmChecked) {
      setBulkError('Check the confirmation box before bulk deleting.');
      return;
    }
    if (String(bulkConfirmText).trim().toUpperCase() !== 'BULK DELETE') {
      setBulkError('Type BULK DELETE (all caps) to confirm bulk deletion.');
      return;
    }

    setBulkDeleteLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', bulkFile);
      fd.append('confirm', 'true');
      fd.append('toleranceMs', String(toleranceMs || '120000'));
      if (activeConnectionId) fd.append('connectionId', activeConnectionId);

      const { res } = await postMultipartWithNetworkFallback('/api/hcm-timecards/bulk-delete', fd);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || data.message || `Bulk delete failed (${res.status})`);
      setBulkPreview(data);
    } catch (e) {
      const attemptedUrls = Array.isArray(e?.attemptedUrls) ? e.attemptedUrls : [];
      const detail = attemptedUrls.length ? ` Attempted: ${attemptedUrls.join(' , ')}` : '';
      setBulkError((e.message || 'Bulk delete failed') + detail);
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  const handleDelete = async () => {
    resetOutcome();
    setDeleteResult(null);

    if (!selectedMatch) {
      setDeleteError('Select exactly one match from the preview results.');
      return;
    }
    if (!confirmChecked) {
      setDeleteError('Check the confirmation box before deleting.');
      return;
    }
    if (String(confirmText).trim().toUpperCase() !== 'DELETE') {
      setDeleteError('Type DELETE (all caps) to enable deletion.');
      return;
    }

    setDeleteLoading(true);
    try {
      const tol = Number(String(toleranceMs || '').trim());
      const body = {
        confirm: true,
        personNumber: personNumber.trim(),
        startDate: startDate.trim(),
        stopDate: stopDate.trim(),
        toleranceMs: Number.isFinite(tol) ? tol : 120000,
        timeCardId: selectedMatch.timeCardId,
        timeCardLineId: selectedMatch.timeCardLineId || '',
        fingerprint: selectedMatch.fingerprint,
      };
      const dst = startTime.trim();
      const det = stopTime.trim();
      if (dst && det) {
        body.startTime = dst;
        body.stopTime = det;
      }
      if (activeConnectionId) body.connectionId = activeConnectionId;

      const { res } = await postJsonWithNetworkFallback('/api/hcm-timecards/delete', body);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || data.message || `Delete failed (${res.status})`);
      }
      setDeleteResult(data);
    } catch (e) {
      const attemptedUrls = Array.isArray(e?.attemptedUrls) ? e.attemptedUrls : [];
      const detail = attemptedUrls.length ? ` Attempted: ${attemptedUrls.join(' , ')}` : '';
      setDeleteError((e.message || 'Delete failed') + detail);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="max-w-4xl space-y-6">
      <div className="card-panel p-6">
        <h1 className="text-xl font-semibold text-slate-800">Delete HCM time cards (REST)</h1>
        <p className="mt-2 text-sm text-slate-600">
          This uses Oracle HCM REST <span className="font-mono">timeCards</span> / <span className="font-mono">timeCardLines</span>.
          With <strong>person number</strong> and <strong>start date</strong> only, preview lists every line that overlaps that calendar day (or <strong>StartDate</strong> through optional <strong>StopDate</strong>). Add <strong>start and stop time of day</strong> to narrow to a single segment match. Delete always uses the line you select from the preview.
        </p>
        <p className="mt-2 text-xs text-slate-500">
          <span className="font-semibold">StartDate</span> must be <span className="font-mono">YYYY-MM-DD</span>. <span className="font-semibold">StopDate</span> is optional; with times omitted it extends the listing range; with times, when StopDate is omitted the stop time is interpreted on the same calendar day as StartDate.
        </p>
        <p className="mt-2 text-xs text-slate-500">
          Use <strong>hours and minutes only</strong> for times of day: <span className="font-mono">13:00</span> → <span className="font-mono">21:00</span> (24-hour{' '}
          <span className="font-mono">HH:mm</span>). Seconds are optional if you prefer <span className="font-mono">13:00:00</span>. Full Oracle ISO with offset is still accepted (e.g.{' '}
          <span className="font-mono">2026-02-16T13:00:00.000+00:00</span>). If you omit a timezone on a bare time, the server parses it in the{' '}
          <strong>Node process local timezone</strong> (often UTC on Linux/Heroku). If matching is off by hours, include an explicit offset.
        </p>

        <div className="mt-4 inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1">
          <button
            type="button"
            onClick={() => {
              setMode('single');
              resetBulkOutcome();
              setBulkFile(null);
              if (bulkInputRef.current) bulkInputRef.current.value = '';
            }}
            className={`rounded-md px-3 py-1.5 text-sm font-medium ${
              mode === 'single' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Single delete
          </button>
          <button
            type="button"
            onClick={() => {
              setMode('bulk');
              resetOutcome();
              setPreview(null);
              setSelectedMatchKey('');
              setConfirmChecked(false);
              setConfirmText('');
            }}
            className={`rounded-md px-3 py-1.5 text-sm font-medium ${
              mode === 'bulk' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Bulk delete (Excel)
          </button>
        </div>

        {!hasOracleTarget && (
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            Log in to Oracle or add a saved connection before using this tool.
          </div>
        )}

        {hasOracleTarget && (
          <div className="mt-4 rounded-lg border-2 border-cyan-200 bg-cyan-50/50 p-4">
            <p className="text-sm font-semibold text-cyan-900 mb-1">Oracle target</p>
            {user ? (
              <p className="text-sm font-medium text-slate-800 break-all" title={user.instanceUrl || 'Oracle instance from your sign-in'}>
                {user.instanceUrl || 'Oracle instance (from your sign-in)'}
              </p>
            ) : connectionsList?.length > 0 ? (
              <>
                {connectionsList.length === 1 ? (
                  <p className="text-sm font-medium text-slate-800 break-all" title={activeConnection?.application_hcm_url}>
                    {activeConnection?.application_hcm_url || '—'}
                  </p>
                ) : (
                  <select
                    value={activeConnectionId || ''}
                    onChange={(e) => onSelectConnectionId?.(e.target.value || null)}
                    className="w-full max-w-2xl rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800"
                  >
                    {connectionsList.map((conn) => (
                      <option key={conn.id} value={conn.id}>
                        {conn.connection_name || 'Unnamed'} — {conn.application_hcm_url || ''}
                      </option>
                    ))}
                  </select>
                )}
                {activeConnection && (
                  <p className="text-xs text-slate-600 mt-1">
                    Connection: {activeConnection.connection_name || 'Unnamed'}
                    {activeConnection.username && ` · User: ${activeConnection.username}`}
                  </p>
                )}
              </>
            ) : null}
          </div>
        )}

        {mode === 'single' ? (
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Person number</label>
              <input
                value={personNumber}
                onChange={(e) => setPersonNumber(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900"
                placeholder="53000417"
                disabled={!hasOracleTarget}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Start date (YYYY-MM-DD)</label>
              <input
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm text-slate-900"
                placeholder="2026-02-16"
                disabled={!hasOracleTarget}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Stop date (optional, YYYY-MM-DD)</label>
              <input
                value={stopDate}
                onChange={(e) => setStopDate(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm text-slate-900"
                placeholder="Same as start date"
                disabled={!hasOracleTarget}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Start time (optional, HH:mm or ISO)</label>
              <input
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm text-slate-900"
                placeholder="13:00"
                disabled={!hasOracleTarget}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Stop time (optional, HH:mm or ISO)</label>
              <input
                value={stopTime}
                onChange={(e) => setStopTime(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm text-slate-900"
                placeholder="21:00"
                disabled={!hasOracleTarget}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Match tolerance (ms)</label>
              <input
                value={toleranceMs}
                onChange={(e) => setToleranceMs(e.target.value)}
                className="w-full max-w-xs rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm text-slate-900"
                disabled={!hasOracleTarget}
              />
              <p className="mt-1 text-xs text-slate-500">
                Useful when UI copy/paste rounds timestamps. Default 120000 = 2 minutes.
              </p>
            </div>
          </div>
        ) : (
          <div className="mt-6 space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Excel file</label>
              <input
                ref={bulkInputRef}
                type="file"
                accept=".xlsx,.xls"
                disabled={!hasOracleTarget}
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  setBulkFile(f);
                  resetBulkOutcome();
                }}
                className="block w-full text-sm text-slate-700"
              />
              <p className="mt-2 text-xs text-slate-600">
                Expected columns (header row): <span className="font-mono">PersonNumber</span>, <span className="font-mono">StartDate</span>{' '}
                (<span className="font-mono">YYYY-MM-DD</span>), optional <span className="font-mono">StopDate</span>,{' '}
                <span className="font-mono">StartTime</span>, <span className="font-mono">StopTime</span> (time-of-day{' '}
                <span className="font-mono">HH:mm</span>; seconds optional, or full ISO). Bulk delete only processes rows with{' '}
                <strong>exactly one</strong> matching time card line.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Match tolerance (ms)</label>
              <input
                value={toleranceMs}
                onChange={(e) => setToleranceMs(e.target.value)}
                className="w-full max-w-xs rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm text-slate-900"
                disabled={!hasOracleTarget}
              />
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={handleBulkPreview}
                disabled={!hasOracleTarget || bulkPreviewLoading || !bulkFile}
                className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
              >
                {bulkPreviewLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {bulkPreviewLoading ? 'Scanning…' : 'Preview bulk deletes'}
              </button>
            </div>

            {bulkError && (
              <div className="flex items-start gap-2 rounded-lg bg-red-50 p-4 text-sm text-red-800 border border-red-200">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
                <div className="min-w-0 flex-1 whitespace-pre-line break-words">{bulkError}</div>
              </div>
            )}

            {bulkPreview && (
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <p className="text-sm font-semibold text-slate-900 mb-2">Bulk preview summary</p>
                <pre className="whitespace-pre-wrap break-all text-xs">{JSON.stringify(bulkPreview.summary, null, 2)}</pre>

                <div className="mt-4 max-h-[360px] overflow-auto border border-slate-200 rounded-lg">
                  <table className="min-w-full divide-y divide-slate-200 text-xs">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold text-slate-700">Excel #</th>
                        <th className="px-3 py-2 text-left font-semibold text-slate-700">Status</th>
                        <th className="px-3 py-2 text-left font-semibold text-slate-700">PN</th>
                        <th className="px-3 py-2 text-left font-semibold text-slate-700">Dates</th>
                        <th className="px-3 py-2 text-left font-semibold text-slate-700">Note</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 bg-white">
                      {(bulkPreview.results || []).map((r) => (
                        <tr key={`${r.excelRowNumber}-${r.status}`}>
                          <td className="px-3 py-2 font-mono text-slate-700">{r.excelRowNumber}</td>
                          <td className="px-3 py-2 font-semibold text-slate-900">{r.status}</td>
                          <td className="px-3 py-2 font-mono text-slate-700">{r.personNumber || '—'}</td>
                          <td className="px-3 py-2 font-mono text-[11px] text-slate-700 whitespace-pre-wrap break-all">
                            {r.input?.startDate || '—'}
                            {r.input?.stopDate ? `\n→ ${r.input.stopDate}` : ''}
                          </td>
                          <td className="px-3 py-2 text-slate-700">
                            {r.status === 'ambiguous'
                              ? `Multiple matches (${r.matches?.length || 0}) — not deleted in bulk`
                              : r.status === 'no_match'
                                ? 'No match'
                                : r.status === 'ready'
                                  ? `Will delete ${r.selected?.timeCardLineId ? 'timeCardLine' : 'timeCard'}`
                                  : r.error || r.reason || ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="mt-6 rounded-xl border border-rose-200 bg-rose-50/40 p-4">
                  <p className="text-sm font-semibold text-rose-950 mb-3">Bulk delete (Excel)</p>
                  <label className="flex items-start gap-2 text-sm text-rose-950">
                    <input
                      type="checkbox"
                      checked={bulkConfirmChecked}
                      onChange={(e) => setBulkConfirmChecked(e.target.checked)}
                      className="mt-1"
                    />
                    <span>
                      I understand this will delete <strong>all “ready”</strong> rows from the last preview for this same uploaded file, and will skip ambiguous/no-match rows.
                    </span>
                  </label>

                  <div className="mt-3">
                    <label className="block text-sm font-medium text-rose-950 mb-1">Type BULK DELETE to confirm</label>
                    <input
                      value={bulkConfirmText}
                      onChange={(e) => setBulkConfirmText(e.target.value)}
                      className="w-full max-w-sm rounded-lg border border-rose-200 bg-white px-3 py-2 text-sm text-slate-900"
                      placeholder="BULK DELETE"
                    />
                  </div>

                  <button
                    type="button"
                    onClick={handleBulkDelete}
                    disabled={bulkDeleteLoading || !bulkFile}
                    className="mt-4 inline-flex items-center gap-2 rounded-lg bg-rose-700 px-4 py-2.5 text-sm font-medium text-white hover:bg-rose-800 disabled:opacity-50"
                  >
                    {bulkDeleteLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                    {bulkDeleteLoading ? 'Deleting…' : 'Run bulk delete'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {mode === 'single' && (
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={handlePreview}
            disabled={!hasOracleTarget || previewLoading}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
          >
            {previewLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {previewLoading ? 'Searching…' : 'Preview list / matches'}
          </button>
        </div>
        )}

        {mode === 'single' && previewError && (
          <div className="mt-4 flex items-start gap-2 rounded-lg bg-red-50 p-4 text-sm text-red-800 border border-red-200">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
            <div className="min-w-0 flex-1 whitespace-pre-line break-words">{previewError}</div>
          </div>
        )}

        {mode === 'single' && preview && (
          <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4">
            <p className="text-sm font-semibold text-slate-900 mb-2">Preview results</p>
            <p className="text-xs text-slate-600 mb-3">
              Window searched: <span className="font-mono">{preview.window?.startDate}</span> →{' '}
              <span className="font-mono">{preview.window?.endDate}</span>
              {preview.oracle?.timeCardLineChildFetches != null ? (
                <>
                  {' '}
                  · Line child fetches: <span className="font-mono">{preview.oracle.timeCardLineChildFetches}</span>
                </>
              ) : null}
            </p>
            {preview.input && (
              <p className="text-xs text-slate-600 mb-3">
                {preview.input.dateListMode ? (
                  <>
                    Date filter (selected calendar):{' '}
                    <span className="font-mono">{String(preview.input.listUserRangeStart || '')}</span> →{' '}
                    <span className="font-mono">{String(preview.input.listUserRangeEnd || '')}</span>
                    {preview.input.listUtcOverlapWindow ? (
                      <>
                        {' '}
                        · list overlap (UTC, includes overnight slack):{' '}
                        <span className="font-mono">
                          {preview.input.listUtcOverlapWindow.start} → {preview.input.listUtcOverlapWindow.end}
                        </span>
                      </>
                    ) : null}
                  </>
                ) : (
                  <>
                    Resolved match timestamps:{' '}
                    <span className="font-mono break-all">{String(preview.input.resolvedStartTime || '')}</span> →{' '}
                    <span className="font-mono break-all">{String(preview.input.resolvedStopTime || '')}</span>
                  </>
                )}
              </p>
            )}
            {preview.listTruncated ? (
              <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-2 mb-3">
                List was truncated to the first {preview.matches?.length ?? 0} line(s). Narrow with start/stop times or shorten the date range, or raise{' '}
                <span className="font-mono">ORACLE_HCM_TIMECARD_LIST_CAP</span> on the server.
              </p>
            ) : null}

            {(!preview.matches || preview.matches.length === 0) && (
              <div className="text-sm text-slate-700 space-y-2">
                <p>No matching time card lines were found for those inputs.</p>
                {preview.listNoMatchHint ? (
                  <p className="text-xs text-slate-600 border border-slate-200 rounded-lg p-3 bg-slate-50 whitespace-pre-line">
                    {preview.listNoMatchHint}
                  </p>
                ) : null}
              </div>
            )}

            {preview.matches && preview.matches.length > 1 && (
              <div className="space-y-2">
                <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  Multiple matches found. Select the exact row to delete.
                </p>
                <div className="space-y-2 max-h-[320px] overflow-auto pr-1">
                  {preview.matches.map((m) => (
                    <label
                      key={m.fingerprint}
                      className="flex gap-3 rounded-lg border border-slate-200 p-3 hover:bg-slate-50 cursor-pointer"
                    >
                      <input
                        type="radio"
                        name="hcm-timecard-match"
                        checked={String(selectedMatchKey) === String(m.fingerprint)}
                        onChange={() => setSelectedMatchKey(String(m.fingerprint))}
                      />
                      <div className="min-w-0 text-sm">
                        <p className="font-mono text-xs text-slate-500 break-all">fingerprint: {m.fingerprint}</p>
                        <p className="mt-1 text-slate-900">
                          <span className="font-semibold">PN:</span> {m.personNumber}{' '}
                          <span className="font-semibold ml-2">timeCardId:</span> {m.timeCardId}{' '}
                          <span className="font-semibold ml-2">lineId:</span> {m.timeCardLineId || '(none — card-level)'}
                        </p>
                        <p className="mt-1 font-mono text-xs text-slate-700 break-all">
                          {String(m.startTime)} → {String(m.stopTime)}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {preview.matches && preview.matches.length === 1 && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
                <p className="font-semibold text-slate-900">Single match</p>
                <p className="mt-1 font-mono text-xs text-slate-600 break-all">fingerprint: {preview.matches[0].fingerprint}</p>
                <p className="mt-2 text-slate-800">
                  <span className="font-semibold">PN:</span> {preview.matches[0].personNumber}{' '}
                  <span className="font-semibold ml-2">timeCardId:</span> {preview.matches[0].timeCardId}{' '}
                  <span className="font-semibold ml-2">lineId:</span>{' '}
                  {preview.matches[0].timeCardLineId || '(none — will delete whole timeCard)'}
                </p>
                <p className="mt-2 font-mono text-xs text-slate-800 break-all">
                  {String(preview.matches[0].startTime)} → {String(preview.matches[0].stopTime)}
                </p>
              </div>
            )}
          </div>
        )}

        {mode === 'single' && preview && Array.isArray(preview.matches) && preview.matches.length > 0 && (
          <div className="mt-6 rounded-xl border border-rose-200 bg-rose-50/40 p-4">
            <p className="text-sm font-semibold text-rose-950 mb-3">Danger zone: delete in Oracle</p>

            <label className="flex items-start gap-2 text-sm text-rose-950">
              <input
                type="checkbox"
                checked={confirmChecked}
                onChange={(e) => setConfirmChecked(e.target.checked)}
                className="mt-1"
              />
              <span>
                I understand this permanently deletes production time data in Oracle for the selected match, and I have verified the match is correct.
              </span>
            </label>

            <div className="mt-3">
              <label className="block text-sm font-medium text-rose-950 mb-1">Type DELETE to confirm</label>
              <input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                className="w-full max-w-sm rounded-lg border border-rose-200 bg-white px-3 py-2 text-sm text-slate-900"
                placeholder="DELETE"
              />
            </div>

            <button
              type="button"
              onClick={handleDelete}
              disabled={deleteLoading || !selectedMatch}
              className="mt-4 inline-flex items-center gap-2 rounded-lg bg-rose-700 px-4 py-2.5 text-sm font-medium text-white hover:bg-rose-800 disabled:opacity-50"
            >
              {deleteLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              {deleteLoading ? 'Deleting…' : 'Delete selected match'}
            </button>

            {deleteError && (
              <div className="mt-4 flex items-start gap-2 rounded-lg bg-white p-3 text-sm text-rose-900 border border-rose-200">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
                <div className="min-w-0 flex-1 whitespace-pre-line break-words">{deleteError}</div>
              </div>
            )}

            {deleteResult && (
              <div className="mt-4 rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-900">
                <p className="font-semibold">Deleted</p>
                <pre className="mt-2 whitespace-pre-wrap break-all text-xs">{JSON.stringify(deleteResult, null, 2)}</pre>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
